import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, MessageCircle, Instagram, Send } from "lucide-react";
import Logo from "@/components/Logo";

const ADDRESS = "Sudimoro RT 01, RW 01, Kelor, Karangmojo, Gunungkidul, DI Yogyakarta";
const WA_NUMBER = "0823-4230-2515";
const WA_LINK = "https://wa.me/6282342302515?text=" + encodeURIComponent("Halo RESADA, saya tertarik mencoba 🙂");
const EMAIL = "hello@resada.id";
const COMMUNITY_URL = "https://chat.whatsapp.com/CwROTH1TJUoG5X9PJrkDQz?mode=gi_t";

const PRODUK = [
  { to: "/#fitur", label: "Fitur" },
  { to: "/#harga", label: "Harga" },
  { to: "/#cara", label: "Cara Kerja" },
  { to: "/blog", label: "Blog" },
  { to: "/login", label: "Mulai Gratis" },
];

const PERUSAHAAN = [
  { to: "/about", label: "Tentang Kami" },
  { to: "/kontak", label: "Kontak" },
  { to: "/faq", label: "FAQ" },
];

const LEGAL = [
  { to: "/privacy", label: "Kebijakan Privasi" },
  { to: "/terms", label: "Syarat & Ketentuan" },
  { to: "/refund", label: "Pengembalian Dana" },
];

function FooterColumn({ title, items }) {
  return (
    <div>
      <h4 className="font-display font-bold text-sm text-white mb-4">{title}</h4>
      <ul className="space-y-2.5">
        {items.map((it) => (
          <li key={it.to}>
            <Link to={it.to} className="text-sm text-slate-300 hover:text-white transition-colors inline-flex items-center gap-1.5 group">
              <span className="w-1 h-1 rounded-full bg-sky-400 opacity-0 group-hover:opacity-100 transition" />
              {it.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function SiteFooter() {
  return (
    <footer className="bg-slate-900 text-slate-300 mt-20 border-t border-slate-800" data-testid="site-footer">
      {/* Decorative gradient strip */}
      <div className="h-1 bg-gradient-to-r from-sky-400 via-violet-400 to-emerald-400" />

      <div className="max-w-7xl mx-auto px-6 pt-14 pb-8">
        <div className="grid gap-10 md:grid-cols-12">
          {/* Brand + description + contact */}
          <div className="md:col-span-5 space-y-5">
            <div className="flex items-center gap-3">
              <Logo to="/" iconSize={36} dark />
            </div>
            <p className="text-sm text-slate-400 leading-relaxed max-w-md" data-testid="footer-description">
              Platform <b className="text-white">scraping leads bisnis Indonesia</b> + <b className="text-white">WhatsApp & Email blast</b> untuk
              freelancer, agency, dan UMKM. Dapatkan data lengkap (telepon, email, alamat) dari
              Google Maps, Facebook, dan Instagram — lalu jalankan outreach personal dalam satu app.
            </p>

            <div className="space-y-2.5 text-sm text-slate-300">
              <a href={`https://maps.google.com/?q=${encodeURIComponent(ADDRESS)}`} target="_blank" rel="noreferrer"
                data-testid="footer-address" className="flex items-start gap-2.5 group hover:text-white transition">
                <MapPin className="w-4 h-4 mt-0.5 text-sky-400 shrink-0" />
                <span className="leading-relaxed">{ADDRESS}</span>
              </a>
              <a href={WA_LINK} target="_blank" rel="noreferrer" data-testid="footer-wa"
                className="flex items-center gap-2.5 hover:text-white transition">
                <MessageCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>WhatsApp · <b>{WA_NUMBER}</b></span>
              </a>
              <a href={`mailto:${EMAIL}`} data-testid="footer-email" className="flex items-center gap-2.5 hover:text-white transition">
                <Mail className="w-4 h-4 text-violet-400 shrink-0" />
                <span>{EMAIL}</span>
              </a>
            </div>

            {/* Social pills */}
            <div className="flex items-center gap-2 pt-1">
              <a href={WA_LINK} target="_blank" rel="noreferrer" title="Chat WhatsApp"
                className="w-9 h-9 rounded-xl bg-slate-800 hover:bg-emerald-500/20 hover:text-emerald-400 grid place-items-center transition">
                <Phone className="w-4 h-4" />
              </a>
              <a href={COMMUNITY_URL} target="_blank" rel="noreferrer" title="Gabung komunitas"
                className="w-9 h-9 rounded-xl bg-slate-800 hover:bg-sky-500/20 hover:text-sky-400 grid place-items-center transition">
                <Send className="w-4 h-4" />
              </a>
              <a href="https://instagram.com/resada.id" target="_blank" rel="noreferrer" title="Instagram"
                className="w-9 h-9 rounded-xl bg-slate-800 hover:bg-violet-500/20 hover:text-violet-400 grid place-items-center transition">
                <Instagram className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Link columns */}
          <div className="md:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8">
            <FooterColumn title="Produk" items={PRODUK} />
            <FooterColumn title="Perusahaan" items={PERUSAHAAN} />
            <FooterColumn title="Legal" items={LEGAL} />
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <p>© {new Date().getFullYear()} <b className="text-slate-300">RESADA</b> — Dibuat untuk freelancer & agency Indonesia.</p>
          <Link to="/register" className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-bold px-5 py-2.5 rounded-xl text-sm transition">
            Coba Gratis Sekarang →
          </Link>
        </div>
      </div>
    </footer>
  );
}
